import { type LucideIcon } from "lucide-react";
import { StoreImage } from "@/components/StoreImage";

// String kelas LITERAL utuh per ukuran — Tailwind JIT hanya men-scan literal.
// Geometri tumpukan mengikuti preview yang disetujui pemilik (2026-10-20):
// kartu belakang miring ke kiri, kartu depan (foto TERBARU) ke kanan.
const SIZES = {
  // Baris kategori beranda / katalog reseller (tile 88px, discroll horizontal).
  row: {
    box: "h-20 w-[5.25rem]",
    back: "left-2 top-1.5 h-[3.75rem] w-12 -rotate-[9deg]",
    front: "left-7 top-3 h-[3.875rem] w-[3.125rem] rotate-[7deg]",
    single: "left-4 top-2 h-16 w-[3.25rem] rotate-[4deg]",
    imgSizes: "56px",
    iconSize: 22,
  },
  // Kartu grid landing `/kategori` (3 kolom).
  landing: {
    box: "h-[5.5rem] w-24",
    back: "left-2.5 top-1.5 h-[4.125rem] w-[3.25rem] -rotate-[9deg]",
    front: "left-[2.125rem] top-3 h-[4.25rem] w-[3.375rem] rotate-[7deg]",
    single: "left-5 top-2 h-[4.375rem] w-14 rotate-[4deg]",
    imgSizes: "64px",
    iconSize: 24,
  },
} as const;

// Tanpa warna latar: tiap cabang memasang latarnya sendiri. Dua utilitas `bg-*`
// pada satu elemen dimenangkan urutan CSS hasil build, bukan urutan kelas.
const CARD = "absolute overflow-hidden rounded-lg border-2 border-white";
// Latar kartu foto selama gambarnya belum termuat.
const PHOTO_BG = "bg-neutral-100";
const SHADOW_BACK = "shadow-[0_2px_6px_rgba(34,33,38,0.18)]";
const SHADOW_FRONT = "shadow-[0_3px_8px_rgba(34,33,38,0.26)]";

/**
 * Gambar tile kategori gaya "tumpukan foto": DUA foto sampul produk terbaru di
 * kategori itu (`categoryCoverPhotos`), kartu depan = produk paling baru.
 * Dipakai baris kategori beranda (`CategoryRow`) dan grid landing `/kategori`
 * (`KategoriClient` + `CatalogKategoriClient`) supaya bentuknya satu.
 *
 * Urutan prioritas:
 * 1. Ikon UNGGAHAN admin (`product_categories.icon_url`, mig 0951) — override
 *    manual yang disengaja, menang atas foto otomatis. Tampil sebagai SATU
 *    kartu, `object-contain` supaya ilustrasi persegi tak terpotong.
 * 2. Foto produk: 2+ foto → tumpukan dua kartu; baru 1 foto → satu kartu.
 * 3. Belum ada foto sama sekali → ikon lucide turunan-nama di atas latar
 *    pastel, tetap berbentuk kartu. `stacked` menambahkan kartu polos di
 *    belakangnya (tile "Semua").
 *
 * Nama kategori sudah dirender sebagai teks di bawah tile oleh pemanggil, jadi
 * semua gambar di sini ber-`alt=""` — kalau tidak, pembaca layar menyebutnya
 * dua kali.
 */
export function CategoryThumb({
  photos,
  iconUrl,
  icon: Icon,
  colorClass,
  size = "row",
  active = false,
  stacked = false,
}: {
  photos: readonly string[];
  /** Ikon unggahan admin; kosong → foto produk / ikon lucide. */
  iconUrl?: string;
  icon: LucideIcon;
  /** Latar pastel + warna ikon lucide (`categoryTileClass`). */
  colorClass: string;
  size?: keyof typeof SIZES;
  /** Sorot kartu depan (mode filter). */
  active?: boolean;
  /** Kartu polos di belakang ikon lucide — hanya tile "Semua". */
  stacked?: boolean;
}) {
  const s = SIZES[size];
  const ring = active ? "ring-2 ring-shopee" : "";

  let content;
  if (iconUrl) {
    content = (
      <span className={`${CARD} ${SHADOW_FRONT} ${s.single} bg-white ${ring}`}>
        <StoreImage
          src={iconUrl}
          alt=""
          fill
          sizes={s.imgSizes}
          className="object-contain"
        />
      </span>
    );
  } else if (photos.length >= 2) {
    content = (
      <>
        <span className={`${CARD} ${SHADOW_BACK} ${s.back} ${PHOTO_BG}`}>
          <StoreImage
            src={photos[1]}
            alt=""
            fill
            sizes={s.imgSizes}
            className="object-cover"
          />
        </span>
        <span className={`${CARD} ${SHADOW_FRONT} ${s.front} ${PHOTO_BG} ${ring}`}>
          <StoreImage
            src={photos[0]}
            alt=""
            fill
            sizes={s.imgSizes}
            className="object-cover"
          />
        </span>
      </>
    );
  } else if (photos.length === 1) {
    content = (
      <span className={`${CARD} ${SHADOW_FRONT} ${s.single} ${PHOTO_BG} ${ring}`}>
        <StoreImage
          src={photos[0]}
          alt=""
          fill
          sizes={s.imgSizes}
          className="object-cover"
        />
      </span>
    );
  } else if (stacked) {
    content = (
      <>
        <span className={`${CARD} ${SHADOW_BACK} ${s.back} bg-shopee/20`} />
        <span
          className={`${CARD} ${SHADOW_FRONT} ${s.front} ${colorClass} grid place-items-center ${ring}`}
        >
          <Icon size={s.iconSize} />
        </span>
      </>
    );
  } else {
    content = (
      <span
        className={`${CARD} ${SHADOW_FRONT} ${s.single} ${colorClass} grid place-items-center ${ring}`}
      >
        <Icon size={s.iconSize} />
      </span>
    );
  }

  return <span className={`relative block shrink-0 ${s.box}`}>{content}</span>;
}
